import type { ThreatReportKey, View } from '@/shared/types';
import { isAdHocCase } from '@/shared/ad-hoc-evidence';
import { PRESENTATION_VIEW_STATE_KEY } from '@/lib/presentation-view-state';
import { setSessionItem } from '@/lib/session-storage';

/**
 * Bookmarkable / refresh-survivable navigation state for the main app shell.
 *
 * URL query params (all optional):
 * - case       — selected case id / tag
 * - view       — chat | table | presentation | courtroom | horizontal |
 *                work-product-index | threat-report | csam-report | settings |
 *                sandbox | cases | case-file-management | account
 *                (`cases` is accepted in URLs and remapped to `account`)
 * - timeline   — selected timeline id
 * - session    — chat session id
 * - event      — zero-based timeline event / slide index
 * - eventModal — 1 when the event editor modal is open
 * - wpMode     — work-product index layout: list (grid is the default and omitted)
 * - threatChat / threatSession / threatEpoch / threatType — threat report identity
 *
 * History:
 * - Significant nav changes (case/view/timeline/session/threat) use pushState so
 *   Back/Forward step through in-session views.
 * - Minor updates (event index, eventModal, wpMode) use replaceState.
 * - Each browser tab gets an app-nav session id (sessionStorage). Login starts a
 *   new session; refresh keeps the same session and history stack.
 * - Logout / tenant switch calls `resetAppNavSession` (clears storage, strips URL
 *   params, wipes history markers). After re-login or a tenant change,
 *   Back/Forward into prior-session entries is ignored so another user/tenant
 *   cannot revive the previous app state.
 */

export const APP_NAV_VIEWS: readonly View[] = [
  'chat',
  'table',
  'presentation',
  'courtroom',
  'horizontal',
  'settings',
  'sandbox',
  'work-product-index',
  'threat-report',
  'csam-report',
  'cases',
  'case-file-management',
  'account',
] as const;

export const APP_NAV_PARAM_KEYS = [
  'case',
  'view',
  'timeline',
  'session',
  'event',
  'eventModal',
  'wpMode',
  'threatChat',
  'threatSession',
  'threatEpoch',
  'threatType',
] as const;

export const APP_NAV_SESSION_KEY = 'taiAppSessionId';
export const APP_NAV_HISTORY_FLAG = 'taiAppNav';
/** Last Main shell pathname (`/` or `/employee/app`) so overlay pages can return. */
export const APP_NAV_SHELL_PATH_KEY = 'taiAppShellPath';
/** View to restore when leaving Account / Case Files via the header back button. */
export const APP_NAV_RETURN_VIEW_KEY = 'taiAppReturnView';

const STORAGE_KEYS = {
  caseId: 'case',
  view: 'view',
  timelineId: 'timeline',
  sessionId: 'chatSession',
  eventIndex: 'eventIndex',
  eventModal: 'eventModal',
  wpMode: 'workProductViewMode',
  threatReportKey: 'threatReportKey',
} as const;

export type AppNavState = {
  caseId?: string;
  view?: View;
  timelineId?: string;
  sessionId?: string;
  eventIndex?: number;
  /** True when the timeline event editor modal should be open. */
  eventModal?: boolean;
  wpMode?: 'grid' | 'list';
  threatReportKey?: ThreatReportKey;
};

/** Views that show a selected timeline and can host the event modal. */
export function isTimelineNavView(view: View | undefined): boolean {
  return view === 'table' || view === 'presentation' || view === 'courtroom' || view === 'horizontal';
}

export type ViewAfterCaseChangeOptions = {
  caseFileManagementEnabled?: boolean;
};

/**
 * View to land on after the user selects a different case.
 *
 * Timeline views are bound to a timeline that will not exist on the new case,
 * so drop back to the work products index.
 * Case-file-management stays put when that experience is enabled. Account is
 * tenant administration, so picking a case there means the user wants to work
 * that case — send them to Assistant. Restore callers skip this and re-apply
 * the saved view.
 */
export function viewAfterCaseChange(
  currentView: View | undefined,
  options: ViewAfterCaseChangeOptions = {},
): View {
  if (options.caseFileManagementEnabled && currentView === 'case-file-management') {
    return 'case-file-management';
  }
  if (isTimelineNavView(currentView)) {
    return 'work-product-index';
  }
  return 'chat';
}

export type AppNavHistoryState = {
  [APP_NAV_HISTORY_FLAG]?: true;
  [APP_NAV_SESSION_KEY]?: string;
  taiAppSessionId?: string;
  taiAppNav?: true;
};

export type PersistAppNavOptions = {
  /** push = Back/Forward step; replace = update current entry (refresh-safe). */
  mode?: 'push' | 'replace';
  sessionId?: string;
};

function isView(value: string): value is View {
  return (APP_NAV_VIEWS as readonly string[]).includes(value);
}

function normalizePathname(pathname: string): string {
  const trimmed = pathname.trim();
  if (!trimmed || trimmed === '/') return '/';
  return trimmed.endsWith('/') ? trimmed.slice(0, -1) : trimmed;
}

/** True for the Main shell routes that persist bookmarkable view state. */
export function isAppShellPath(pathname: string): boolean {
  const path = normalizePathname(pathname);
  return path === '/' || path === '/employee/app';
}

/**
 * Remember the Main shell path so overlay pages (Analytics, tenant switcher)
 * can return to the same `/` or `/employee/app` context.
 * Non-shell paths are ignored so `/analytics` does not overwrite the last Main view.
 */
export function rememberAppShellPath(
  pathname: string,
  storage: Pick<Storage, 'setItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { setItem: () => undefined },
): void {
  if (!isAppShellPath(pathname)) return;
  setSessionItem(storage, APP_NAV_SHELL_PATH_KEY, normalizePathname(pathname));
}

/** Shell path to reopen Main; defaults to `/` (Assistant) when none was stored. */
export function getPreviousAppShellPath(
  storage: Pick<Storage, 'getItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { getItem: () => null },
): string {
  const raw = storage.getItem(APP_NAV_SHELL_PATH_KEY)?.trim();
  if (raw && isAppShellPath(raw)) return normalizePathname(raw);
  return '/';
}

/** In-shell overlays that restore a previous Main view via the header back button. */
export function isOverlayNavView(view: View | undefined): boolean {
  return view === 'account' || view === 'case-file-management';
}

/**
 * Snapshot the view the user is leaving when they open Account or Case Files,
 * so the header back button can restore it (or Assistant when none was stored).
 */
export function rememberViewBeforeOverlay(
  view: View | undefined,
  storage: Pick<Storage, 'setItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { setItem: () => undefined },
): void {
  if (!view) return;
  setSessionItem(storage, APP_NAV_RETURN_VIEW_KEY, view);
}

/** Consume the overlay return view; `'chat'` when missing or invalid. */
export function consumeViewBeforeOverlay(
  storage: Pick<Storage, 'getItem' | 'removeItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { getItem: () => null, removeItem: () => undefined },
): View {
  const raw = storage.getItem(APP_NAV_RETURN_VIEW_KEY)?.trim() || '';
  storage.removeItem(APP_NAV_RETURN_VIEW_KEY);
  if (raw && isView(raw)) return raw;
  return 'chat';
}

function parseEventIndex(raw: string | null | undefined): number | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const n = Number(raw);
  if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) return undefined;
  return n;
}

function parseWpMode(raw: string | null | undefined): 'grid' | 'list' | undefined {
  if (raw === 'grid' || raw === 'list') return raw;
  return undefined;
}

function parseThreatReportKey(params: {
  chatKey?: string | null;
  sessionId?: string | null;
  epoch?: string | null;
  requestType?: string | null;
}): ThreatReportKey | undefined {
  const chatKey = params.chatKey?.trim();
  const sessionId = params.sessionId?.trim();
  const epochRaw = params.epoch?.trim();
  const requestType = params.requestType?.trim();
  if (!chatKey || !sessionId || !epochRaw || !requestType) return undefined;
  const epochTimeSecs = Number(epochRaw);
  if (!Number.isFinite(epochTimeSecs)) return undefined;
  return { chatKey, sessionId, epochTimeSecs, requestType };
}

function threatKeysEqual(
  a: ThreatReportKey | undefined,
  b: ThreatReportKey | undefined,
): boolean {
  if (!a && !b) return true;
  if (!a || !b) return false;
  return (
    a.chatKey === b.chatKey &&
    a.sessionId === b.sessionId &&
    a.epochTimeSecs === b.epochTimeSecs &&
    a.requestType === b.requestType
  );
}

function newSessionId(): string {
  if (typeof crypto !== 'undefined' && typeof crypto.randomUUID === 'function') {
    return crypto.randomUUID();
  }
  if (typeof crypto !== 'undefined' && typeof crypto.getRandomValues === 'function') {
    const bytes = new Uint8Array(8);
    crypto.getRandomValues(bytes);
    const suffix = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join('');
    return `nav-${Date.now()}-${suffix}`;
  }
  throw new Error('Secure random unavailable for app-nav session id');
}

/** Start a fresh in-tab app-nav session (call on login). */
export function startAppNavSession(
  storage: Pick<Storage, 'setItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { setItem: () => undefined },
): string {
  const id = newSessionId();
  setSessionItem(storage, APP_NAV_SESSION_KEY, id);
  return id;
}

/** Reuse the tab's session across refresh; create one if missing. */
export function getOrCreateAppNavSessionId(
  storage: Pick<Storage, 'getItem' | 'setItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { getItem: () => null, setItem: () => undefined },
): string {
  const existing = storage.getItem(APP_NAV_SESSION_KEY)?.trim();
  if (existing) return existing;
  return startAppNavSession(storage);
}

export function getAppNavSessionId(
  storage: Pick<Storage, 'getItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { getItem: () => null },
): string | null {
  return storage.getItem(APP_NAV_SESSION_KEY)?.trim() || null;
}

/** Clear nav session + mirrored keys (call on logout). */
export function clearAppNavSessionStorage(
  storage: Pick<Storage, 'removeItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { removeItem: () => undefined },
): void {
  storage.removeItem(APP_NAV_SESSION_KEY);
  for (const key of Object.values(STORAGE_KEYS)) {
    storage.removeItem(key);
  }
  storage.removeItem('caseIdFromUrl');
  storage.removeItem('lastWorkProductContext');
  storage.removeItem('presentation');
  storage.removeItem(PRESENTATION_VIEW_STATE_KEY);
  storage.removeItem('queryKey');
  storage.removeItem('TimePilotSkewReload');
  storage.removeItem(APP_NAV_SHELL_PATH_KEY);
  storage.removeItem(APP_NAV_RETURN_VIEW_KEY);
}

/**
 * Drop sessionStorage nav mirrors, strip bookmark params from the current URL,
 * and wipe app-nav markers from the current history entry.
 *
 * Call on logout and on in-session tenant/cell switch so the next context cannot
 * Back/Forward or refresh into the previous tenant's case/view/timeline.
 */
export function resetAppNavSession(
  pathname?: string,
  storage: Pick<Storage, 'removeItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { removeItem: () => undefined },
): void {
  clearAppNavSessionStorage(storage);
  if (typeof window === 'undefined') return;

  const path = pathname || window.location.pathname || '/';
  const cleanUrl = buildAppNavUrl(path, {}, window.location.search);
  const prev =
    typeof window.history.state === 'object' && window.history.state
      ? { ...(window.history.state as Record<string, unknown>) }
      : {};
  delete prev[APP_NAV_HISTORY_FLAG];
  delete prev[APP_NAV_SESSION_KEY];
  delete prev.taiAppNav;
  delete prev.taiAppSessionId;
  window.history.replaceState(prev, '', cleanUrl);
}

/** @deprecated Prefer {@link resetAppNavSession}. */
export const resetAppNavForLogout = resetAppNavSession;

/** Prefer primary for each field; fill gaps from fallback. */
export function mergeAppNavState(primary: AppNavState, fallback: AppNavState): AppNavState {
  return {
    caseId: primary.caseId || fallback.caseId,
    view: primary.view ?? fallback.view,
    timelineId: primary.timelineId || fallback.timelineId,
    sessionId: primary.sessionId || fallback.sessionId,
    eventIndex: primary.eventIndex ?? fallback.eventIndex,
    eventModal: primary.eventModal ?? fallback.eventModal,
    wpMode: primary.wpMode ?? fallback.wpMode,
    threatReportKey: primary.threatReportKey ?? fallback.threatReportKey,
  };
}

/**
 * Remove navigation fields that cannot be resolved without a selected case.
 *
 * An orphaned chat session in sessionStorage used to make startup restore that
 * session, reveal the empty chat shell, then write `?session=...`. Next.js
 * treats that history write as a route restore and remounts the shell, producing
 * a Loading → select-a-case loop. Case-scoped fields are not actionable without
 * their case and must not participate in initial restore.
 */
export function normalizeAppNavState(state: AppNavState): AppNavState {
  // Case Management is a section on Account; never persist the retired view.
  const view = state.view === 'cases' ? 'account' : state.view;
  if (state.caseId) return { ...state, view };
  return {
    ...state,
    view,
    timelineId: undefined,
    sessionId: undefined,
    eventIndex: undefined,
    eventModal: undefined,
    threatReportKey: undefined,
  };
}

export function hasAppNavState(state: AppNavState | null | undefined): boolean {
  if (!state) return false;
  return Boolean(
    state.caseId ||
      state.view ||
      state.timelineId ||
      state.sessionId ||
      state.eventIndex != null ||
      state.eventModal ||
      state.wpMode ||
      state.threatReportKey,
  );
}

/**
 * Default shell landing state: chat view, grid work-product layout, nothing else.
 * Persisting / restoring only these values must not block the UI on LaunchDarkly
 * identify (that re-armed "Loading TimePilot…" after the first steady paint).
 */
export function isDefaultAppNavState(state: AppNavState | null | undefined): boolean {
  if (!state) return true;
  if (state.caseId) return false;
  if (state.timelineId) return false;
  if (state.sessionId) return false;
  if (state.eventIndex != null) return false;
  if (state.eventModal) return false;
  if (state.threatReportKey) return false;
  if (state.view && state.view !== 'chat') return false;
  if (state.wpMode && state.wpMode !== 'grid') return false;
  return true;
}

/** True when restore/apply must run (and may wait on LD) before revealing the shell. */
export function needsAppNavRestore(state: AppNavState | null | undefined): boolean {
  return hasAppNavState(state) && !isDefaultAppNavState(state);
}

/**
 * Major navigation that should create a Back/Forward history entry.
 *
 * Intentionally excludes:
 * - event / eventModal / wpMode — high-churn UI details (replaceState only)
 * - session — often hydrates after case load and would create duplicate entries
 */
export function isSignificantNavChange(prev: AppNavState, next: AppNavState): boolean {
  return (
    (prev.caseId || undefined) !== (next.caseId || undefined) ||
    (prev.view || undefined) !== (next.view || undefined) ||
    (prev.timelineId || undefined) !== (next.timelineId || undefined) ||
    !threatKeysEqual(prev.threatReportKey, next.threatReportKey)
  );
}

/** Quiet window used to collapse multi-step nav (e.g. timeline clear→reload) into one history entry. */
export const APP_NAV_HISTORY_COALESCE_MS = 200;

export function appNavStatesEqual(a: AppNavState, b: AppNavState): boolean {
  return (
    !isSignificantNavChange(a, b) &&
    (a.sessionId || undefined) === (b.sessionId || undefined) &&
    (a.eventIndex ?? undefined) === (b.eventIndex ?? undefined) &&
    !!a.eventModal === !!b.eventModal &&
    (a.wpMode || undefined) === (b.wpMode || undefined)
  );
}

function parseEventModal(raw: string | null | undefined): boolean | undefined {
  if (raw == null || raw.trim() === '') return undefined;
  const v = raw.trim().toLowerCase();
  if (v === '1' || v === 'true' || v === 'yes') return true;
  if (v === '0' || v === 'false' || v === 'no') return false;
  return undefined;
}

export function parseAppNavFromSearchParams(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): AppNavState {
  const caseId = searchParams.get('case')?.trim() || undefined;
  const viewRaw = searchParams.get('view')?.trim() || '';
  const view = viewRaw && isView(viewRaw) ? viewRaw : undefined;
  const timelineId = searchParams.get('timeline')?.trim() || undefined;
  const sessionId = searchParams.get('session')?.trim() || undefined;
  const eventIndex = parseEventIndex(searchParams.get('event'));
  const eventModal = parseEventModal(searchParams.get('eventModal'));
  const wpMode = parseWpMode(searchParams.get('wpMode'));
  const threatReportKey = parseThreatReportKey({
    chatKey: searchParams.get('threatChat'),
    sessionId: searchParams.get('threatSession'),
    epoch: searchParams.get('threatEpoch'),
    requestType: searchParams.get('threatType'),
  });

  return {
    caseId,
    view,
    timelineId,
    sessionId,
    eventIndex,
    eventModal,
    wpMode,
    threatReportKey,
  };
}

function hasAppNavSearchParam(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
): boolean {
  return APP_NAV_PARAM_KEYS.some((key) => searchParams.get(key) != null);
}

export function parseAppNavFromSessionStorage(
  storage: Pick<Storage, 'getItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { getItem: () => null },
): AppNavState {
  const caseId = storage.getItem(STORAGE_KEYS.caseId)?.trim() || undefined;
  const viewRaw = storage.getItem(STORAGE_KEYS.view)?.trim() || '';
  const view = viewRaw && isView(viewRaw) ? viewRaw : undefined;
  const timelineId = storage.getItem(STORAGE_KEYS.timelineId)?.trim() || undefined;
  const sessionId = storage.getItem(STORAGE_KEYS.sessionId)?.trim() || undefined;
  const eventIndex = parseEventIndex(storage.getItem(STORAGE_KEYS.eventIndex));
  const eventModal = parseEventModal(storage.getItem(STORAGE_KEYS.eventModal));
  const wpMode = parseWpMode(storage.getItem(STORAGE_KEYS.wpMode));

  let threatReportKey: ThreatReportKey | undefined;
  const threatRaw = storage.getItem(STORAGE_KEYS.threatReportKey);
  if (threatRaw) {
    try {
      const parsed = JSON.parse(threatRaw) as Partial<ThreatReportKey>;
      threatReportKey = parseThreatReportKey({
        chatKey: parsed.chatKey,
        sessionId: parsed.sessionId,
        epoch: parsed.epochTimeSecs != null ? String(parsed.epochTimeSecs) : null,
        requestType: parsed.requestType,
      });
    } catch {
      threatReportKey = undefined;
    }
  }

  return {
    caseId,
    view,
    timelineId,
    sessionId,
    eventIndex,
    eventModal,
    wpMode,
    threatReportKey,
  };
}

/**
 * Whether URL nav params should be applied for this history entry.
 *
 * - No `taiAppNav` marker (null state / hard link / bookmark) → trust URL.
 * - Marked app-nav entry → only trust when its session id matches this tab's
 *   current app-nav session. After logout the session id is cleared, so Back
 *   into a prior login's entries will not restore that tenant/user's state.
 */
export function shouldTrustAppNavUrl(
  historyState: unknown,
  sessionId: string | null,
): boolean {
  if (!historyState || typeof historyState !== 'object') return true;
  const typed = historyState as AppNavHistoryState;
  if (typed.taiAppNav !== true && typed[APP_NAV_HISTORY_FLAG] !== true) {
    return true;
  }
  return isCurrentAppNavHistoryEntry(historyState, sessionId);
}

/** Resolve restore target: URL wins per-field; sessionStorage fills gaps. */
export function resolveAppNavRestoreState(
  searchParams: URLSearchParams | { get: (key: string) => string | null },
  storage?: Pick<Storage, 'getItem'>,
  historyState?: unknown,
  sessionId?: string | null,
): AppNavState {
  let fromUrl = parseAppNavFromSearchParams(searchParams);
  const hasUrlNavParams = hasAppNavSearchParam(searchParams);
  const resolvedSessionId =
    sessionId !== undefined
      ? sessionId
      : getAppNavSessionId(
          storage ??
            (typeof sessionStorage !== 'undefined'
              ? sessionStorage
              : { getItem: () => null }),
        );

  if (
    historyState !== undefined &&
    hasAppNavState(fromUrl) &&
    !shouldTrustAppNavUrl(historyState, resolvedSessionId)
  ) {
    fromUrl = {};
  }

  if (hasUrlNavParams && hasAppNavState(fromUrl)) {
    fromUrl = {
      ...fromUrl,
      view: fromUrl.view ?? 'chat',
      wpMode: fromUrl.wpMode ?? 'grid',
    };
  }

  return normalizeAppNavState(
    mergeAppNavState(fromUrl, parseAppNavFromSessionStorage(storage)),
  );
}

export function buildAppNavSearchParams(
  state: AppNavState,
  existing?: URLSearchParams | string,
): URLSearchParams {
  const params =
    typeof existing === 'string'
      ? new URLSearchParams(existing)
      : existing
        ? new URLSearchParams(existing.toString())
        : new URLSearchParams();

  for (const key of APP_NAV_PARAM_KEYS) {
    params.delete(key);
  }

  if (state.caseId) params.set('case', state.caseId);
  // Omit defaults so `/` → `/?view=chat` (and wpMode=grid) does not rewrite the
  // address bar on first paint — Next patches replaceState and that churn remounted
  // the shell into a second "Loading TimePilot…" flash.
  if (state.view && state.view !== 'chat') params.set('view', state.view);
  if (state.timelineId) params.set('timeline', state.timelineId);
  if (state.sessionId) params.set('session', state.sessionId);
  if (state.eventIndex != null && state.eventIndex >= 0) {
    params.set('event', String(state.eventIndex));
  }
  if (state.eventModal) params.set('eventModal', '1');
  if (state.wpMode && state.wpMode !== 'grid') params.set('wpMode', state.wpMode);
  if (state.threatReportKey) {
    params.set('threatChat', state.threatReportKey.chatKey);
    params.set('threatSession', state.threatReportKey.sessionId);
    params.set('threatEpoch', String(state.threatReportKey.epochTimeSecs));
    params.set('threatType', state.threatReportKey.requestType);
  }

  return params;
}

export function buildAppNavUrl(pathname: string, state: AppNavState, existingSearch?: string): string {
  const params = buildAppNavSearchParams(state, existingSearch ?? '');
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

export function writeAppNavToSessionStorage(
  state: AppNavState,
  storage: Pick<Storage, 'setItem' | 'removeItem'> = typeof sessionStorage !== 'undefined'
    ? sessionStorage
    : { setItem: () => undefined, removeItem: () => undefined },
): void {
  // Nav state is a restore convenience, and this runs from effects on every
  // navigation, so a full session store must not throw into the error boundary.
  const write = (key: string, value: string | undefined) => {
    if (value != null && value !== '') setSessionItem(storage, key, value);
    else storage.removeItem(key);
  };

  write(STORAGE_KEYS.caseId, state.caseId);
  write(STORAGE_KEYS.view, state.view);
  write(STORAGE_KEYS.timelineId, state.timelineId);
  write(STORAGE_KEYS.sessionId, state.sessionId);
  write(
    STORAGE_KEYS.eventIndex,
    state.eventIndex != null && state.eventIndex >= 0 ? String(state.eventIndex) : undefined,
  );
  write(STORAGE_KEYS.eventModal, state.eventModal ? '1' : undefined);
  write(STORAGE_KEYS.wpMode, state.wpMode);
  write(
    STORAGE_KEYS.threatReportKey,
    state.threatReportKey ? JSON.stringify(state.threatReportKey) : undefined,
  );
}

function buildHistoryState(sessionId: string): AppNavHistoryState {
  const prev =
    typeof window !== 'undefined' &&
    typeof window.history.state === 'object' &&
    window.history.state
      ? (window.history.state as Record<string, unknown>)
      : {};
  return {
    ...prev,
    taiAppNav: true,
    taiAppSessionId: sessionId,
  };
}

/**
 * Sync navigation state into the address bar without a Next.js navigation
 * (avoids re-render loops from useSearchParams).
 *
 * When the href is unchanged, update history state only (no `url` argument).
 * Next.js patches `history.replaceState` and dispatches ACTION_RESTORE whenever
 * a url is passed — even for a same-href write — which can remount the app shell.
 */
export function persistAppNavState(
  pathname: string,
  state: AppNavState,
  options: PersistAppNavOptions = {},
): void {
  writeAppNavToSessionStorage(state);
  rememberAppShellPath(pathname);
  if (typeof window === 'undefined') return;

  const mode = options.mode ?? 'replace';
  const sessionId = options.sessionId ?? getOrCreateAppNavSessionId();
  const next = buildAppNavUrl(pathname, state, window.location.search);
  const current = `${window.location.pathname}${window.location.search}`;
  const historyState = buildHistoryState(sessionId);

  if (current === next) {
    // Omit url so Next's history patch does not fire ACTION_RESTORE.
    window.history.replaceState(historyState, '');
    return;
  }

  if (mode === 'push') {
    window.history.pushState(historyState, '', next);
  } else {
    window.history.replaceState(historyState, '', next);
  }
}

/** @deprecated Prefer persistAppNavState(..., { mode: 'replace' }). */
export function replaceAppNavUrl(pathname: string, state: AppNavState): void {
  persistAppNavState(pathname, state, { mode: 'replace' });
}

/** True when this history entry belongs to the current in-tab app-nav session. */
export function isCurrentAppNavHistoryEntry(
  historyState: unknown,
  sessionId: string | null,
): boolean {
  if (!sessionId) return false;
  if (!historyState || typeof historyState !== 'object') return false;
  const typed = historyState as AppNavHistoryState;
  if (typed.taiAppNav !== true && typed[APP_NAV_HISTORY_FLAG] !== true) {
    return false;
  }
  const entrySession = typed.taiAppSessionId ?? typed[APP_NAV_SESSION_KEY];
  // Require a session tag — untagged entries must not survive logout → re-login.
  if (!entrySession) return false;
  return entrySession === sessionId;
}

/**
 * If the current location carries app-nav URL params from a prior login's
 * history entry, strip them so refresh/re-login cannot rehydrate that state.
 * Returns true when the URL was cleaned.
 */
export function discardStaleAppNavUrl(
  pathname?: string,
  historyState: unknown = typeof window !== 'undefined' ? window.history.state : null,
  sessionId: string | null = typeof window !== 'undefined' ? getAppNavSessionId() : null,
): boolean {
  if (typeof window === 'undefined') return false;
  if (shouldTrustAppNavUrl(historyState, sessionId)) return false;

  const fromUrl = parseAppNavFromSearchParams(
    new URLSearchParams(window.location.search),
  );
  if (!hasAppNavState(fromUrl)) return false;

  const path = pathname || window.location.pathname || '/';
  const cleanUrl = buildAppNavUrl(path, {}, window.location.search);
  const prev =
    typeof historyState === 'object' && historyState
      ? { ...(historyState as Record<string, unknown>) }
      : {};
  delete prev[APP_NAV_HISTORY_FLAG];
  delete prev[APP_NAV_SESSION_KEY];
  delete prev.taiAppNav;
  delete prev.taiAppSessionId;
  window.history.replaceState(prev, '', cleanUrl);
  return true;
}

export type SanitizeViewOptions = {
  courtroomMode?: boolean;
  horizontalTimeline?: boolean;
  canOpenDashboard?: boolean;
  /** When false, leave ``csam-report`` as-is until credentials and LD settle. */
  csamAccessReady?: boolean;
  sandbox?: boolean;
  caseManagementDashboard?: boolean;
  caseFileManagement?: boolean;
  /** Tenant-admin Account view. Omit to leave Account as-is; pass false to bounce to chat. */
  accountAllowed?: boolean;
  caseId?: string;
  /** Backend id for the signed-in user's My Files case (token email). */
  personalFilesCaseId?: string | null;
};

/**
 * Apply feature-flag and CSAM-credential guards when restoring a view
 * (refresh / Back / Forward / LaunchDarkly flag updates). Keeps
 * case/timeline/session; only relocates the user out of views the new
 * flags or credentials no longer allow. Do not call this for ``csam-report``
 * or Account until permission-service clearance has settled (`accountAllowed`
 * omitted means leave Account as-is).
 */
export function sanitizeRestoredView(
  view: View | undefined,
  options: SanitizeViewOptions,
): View {
  // Missing view means chat — `buildAppNavSearchParams` omits `view=chat`, so
  // case+chat history/bookmarks are `?case=…` with no view. Do not treat that
  // as work-product-index.
  let next = view ?? 'chat';
  const fallback = options.caseId ? 'work-product-index' : 'chat';

  if (next === 'courtroom' && !options.courtroomMode) {
    next = 'table';
  }
  if (next === 'horizontal' && !options.horizontalTimeline) {
    next = 'table';
  }
  if (next === 'csam-report' && options.csamAccessReady !== false) {
    if (
      !options.canOpenDashboard ||
      isAdHocCase(options.caseId, options.personalFilesCaseId)
    ) {
      next = fallback;
    }
  }
  if (next === 'sandbox' && !options.sandbox) {
    next = 'chat';
  }
  if (next === 'cases') {
    // Case Management now lives on the Account page.
    next = 'account';
  }
  if (next === 'account' && options.accountAllowed === false) {
    next = 'chat';
  }
  if (next === 'case-file-management' && !options.caseFileManagement) {
    next = fallback;
  }
  return next;
}
